import { useState } from 'react';
import { supabase } from '../lib/supabase';

export const useUserProfile = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const applyDarkMode = (enabled) => {
    if (enabled) {
      document.documentElement?.classList?.add('dark');
    } else {
      document.documentElement?.classList?.remove('dark');
    }
    setIsDarkMode(enabled);
  };

  const loadProfile = async (userId) => {
    if (!userId) return;
    setProfileLoading(true);
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
      if (!error && data) {
        setUserProfile(data);
        applyDarkMode(data?.dark_mode ?? false);
      }
    } catch (error) {
      console.error('Profile load error:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  const clearProfile = () => {
    setUserProfile(null);
    setProfileLoading(false);
    applyDarkMode(false);
  };

  const updateProfile = async (userId, updates) => {
    if (!userId) return { error: { message: 'No user logged in' } };
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', userId)?.select()?.single();
      if (!error) setUserProfile(data);
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const toggleDarkMode = async (userId, currentDarkMode) => {
    const next = !currentDarkMode;
    applyDarkMode(next);
    if (userId) {
      try {
        await supabase?.from('user_profiles')?.update({ dark_mode: next, updated_at: new Date()?.toISOString() })?.eq('id', userId);
        setUserProfile((prev) => (prev ? { ...prev, dark_mode: next } : prev));
      } catch (error) {
        console.error('Error saving dark mode preference:', error);
      }
    }
  };

  return {
    userProfile,
    profileLoading,
    isDarkMode,
    loadProfile,
    clearProfile,
    updateProfile,
    toggleDarkMode,
  };
};

export default useUserProfile;
